import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import OperationSidebar from "./OperationSidebar";
import OperationsCardView from "./OperationsCardView";
import OperationsListingView from "./OperationsListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import { softDeleteData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Operations",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_CARD: "Card View",
    VIEW_MODE_LIST: "List View",
    ENTITY_SINGULAR: "Operation",
    ENTITY_PLURAL: "Operations",
    DELETE_TITLE_SINGLE: (name) => `Delete Operation ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Operations?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete operation "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (count) => `You are about to delete ${count} operation(s).

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total operations selected: ${count}`,
    DEACTIVATE_TITLE_SINGLE: (name) => `Deactivate Operation ${name}?`,
    DEACTIVATE_TITLE_BULK: (count) => `Deactivate ${count} Operations?`,
    DEACTIVATE_WARNING: `Operations are shared across Machines, Parts and Job Schedules.

Deactivating hides this operation from new selections. Anything already using it keeps working and is not changed.`,
    DEACTIVATE_CONFIRM: "Deactivate",
    DEACTIVATE_CANCEL: "Cancel",
    ERROR_NO_USER: "No current user in context",
    NOT_FOUND: "Operation not found",
};

const Operations = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeOperationId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();

    const { can } = usePermissions();
    const canWrite   = can("operation", "WRITE");
    const canDestroy = can("operation", "DESTROY");

    const GET_OPERATIONS_LIST_FOR_PLANT = gql`
        query GetOperations($where: OperationWhereInput!) {
            operations(where: $where) {
                id
                name
                description
                is_productive
                standard_duration
                is_active
                deleted_at
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
            }
        }
    `;

    const UPDATE_MANY_OPERATION_MUTATION = gql`
        mutation UpdateManyOperation($data: OperationUpdateManyMutationInput!, $where: OperationWhereInput) {
            updateManyOperation(data: $data, where: $where) {
                count
            }
        }
    `;

    const DELETE_ONE_OPERATION_MUTATION = gql`
        mutation DeleteOneOperation($where: OperationWhereUniqueInput!) {
            deleteOneOperation(where: $where) {
                id
                deleted_at
            }
        }
    `;

    const DELETE_MANY_OPERATIONS_MUTATION = gql`
        mutation DeleteManyOperations($ids: [Int!]!) {
            deleteManyOperation(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const CREATE_ONE_OPERATION_MUTATION = gql`
        mutation CreateOneOperation($data: OperationCreateInput!) {
            createOneOperation(data: $data) {
                id
                name
                description
            }
        }
    `;

    const UPDATE_ONE_OPERATION_MUTATION = gql`
        mutation UpdateOneOperation(
            $data: OperationUpdateInput!
            $where: OperationWhereUniqueInput!
        ) {
            updateOneOperation(data: $data, where: $where) {
                id
                name
                description
            }
        }
    `;

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const patchSearchParams = (patch, options = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, options);
    };

    const setShowInactive = (value, options = {}) =>
        patchSearchParams({ showInactive: value ? "1" : null }, options);

    const operationQueryWhere = {
        plant_id: { equals: starekContext.lastUsedPlant.id },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };

    const { data, loading, refetch } = useQuery(GET_OPERATIONS_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { where: operationQueryWhere },
    });

    const [deleteOneOperation] = useMutation(DELETE_ONE_OPERATION_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deleteManyOperation] = useMutation(DELETE_MANY_OPERATIONS_MUTATION, {
        client: starekContext.engineClient,
    });
    const [createOneOperation] = useMutation(CREATE_ONE_OPERATION_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateOneOperation] = useMutation(UPDATE_ONE_OPERATION_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateManyOperation] = useMutation(UPDATE_MANY_OPERATION_MUTATION, {
        client: starekContext.engineClient,
    });
    const [togglingActiveId, setTogglingActiveId] = useState(null);
    const [isTogglingActive, setIsTogglingActive] = useState(false);

    const [showAddOperationDrawer, setShowAddOperationDrawer] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [viewMode, setViewMode] = useState("card");
    const [routeEditOperation, setRouteEditOperation] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [toggleCleared, setToggleCleared] = useState(false);
    // Tracks the route id we already widened the query for, so the retry runs at most once.
    const inactiveRetryRef = useRef(null);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "card" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);

    const items = useMemo(() => data?.operations ?? [], [data?.operations]);

    useEffect(() => {
        if (!routeId) {
            setRouteEditOperation(null);
            return;
        }

        if (loading) return;

        const listRoute = buildListRoute("/operations", searchParams);

        if (routeOperationId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditOperation(null);
            navigate(listRoute, { replace: true });
            return;
        }

        const operationToEdit = items.find(
            (operation) => Number(operation?.id) === routeOperationId,
        );

        if (!operationToEdit) {
            // The operation may simply be inactive and filtered out of the default list.
            // Widen the query once before concluding it doesn't exist, so deep links to
            // deactivated operations open instead of bouncing back to the list.
            if (!showInactive && inactiveRetryRef.current !== routeOperationId) {
                inactiveRetryRef.current = routeOperationId;
                const widened = new URLSearchParams(searchParams);
                widened.set("showInactive", "1");
                setSearchParams(widened, { replace: true });
                return;
            }
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditOperation(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditOperation(operationToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeOperationId
                ? current
                : [operationToEdit],
        );
        setShowAddOperationDrawer(true);
    }, [
        routeId,
        routeOperationId,
        loading,
        items,
        showInactive,
        searchParams,
        setSearchParams,
        navigate,
        starekContext,
    ]);

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveOperation = async (operationPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id;

            if (!currentUserId) {
                throw new Error(UI_STRINGS.ERROR_NO_USER);
            }

            if (!operationPayload.id) {
                // Create
                const data = {
                    name: operationPayload.name,
                    description: operationPayload.description || "",
                    is_productive: operationPayload.is_productive,
                    plant: {
                        connect: { id: Number(operationPayload.plant_id) },
                    },
                    created_by: { connect: { id: Number(currentUserId) } },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await createOneOperation({ variables: { data } });
            } else {
                // Update - only send fields that have values
                const data = {
                    updated_by: { connect: { id: Number(currentUserId) } },
                };

                if (operationPayload.name) {
                    data.name = { set: operationPayload.name };
                }

                if (operationPayload.description !== undefined) {
                    data.description = {
                        set: operationPayload.description || "",
                    };
                }

                if (operationPayload.is_productive !== undefined) {
                    data.is_productive = {
                        set: operationPayload.is_productive,
                    };
                }

                await updateOneOperation({
                    variables: { data, where: { id: operationPayload.id } },
                });
            }

            // The sidebar stays open to confirm the save and point at the next screen;
            // it closes when the user picks a next step or presses Done.
            await refetch();
            starekContext.showSuccess(
                operationPayload.id
                    ? `Operation "${operationPayload.name}" updated successfully`
                    : `Operation "${operationPayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/operations/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setRouteEditOperation(null);
        setSelectedRows([]);
        setShowAddOperationDrawer(true);
    };

    const handleEditSelected = () => {
        if (selectedRows.length !== 1) return;
        setRouteEditOperation(null);
        setShowAddOperationDrawer(true);
    };

    const handleDelete = (operations) => {
        if (!operations || operations.length === 0 || isDeleting) return;

        const isSingle = operations.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(operations[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(operations.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(operations[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(operations.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(operations.length)}
                        &nbsp;
                        <span className="badge bg-info">
                            {operations.length}
                        </span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                const ids = operations.map((o) => o.id);

                try {
                    // Try batch delete first
                    await deleteManyOperation({ variables: { ids } });
                    setDeleteDone(ids.length);
                    starekContext.showSuccess(
                        `Successfully deleted ${ids.length} operation(s)`,
                    );
                } catch {
                    // Fallback to individual deletes
                    for (const id of ids) {
                        try {
                            await deleteOneOperation({
                                variables: { where: { id } },
                            });
                            setDeleteDone((d) => d + 1);
                        } catch (err) {
                            starekContext.showError?.(err);
                            break;
                        }
                    }
                } finally {
                    setIsDeleting(false);
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddOperationDrawer(false);
        setRouteEditOperation(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            navigate(buildListRoute("/operations", searchParams), {
                replace: true,
            });
        }

        await new Promise((resolve) => setTimeout(resolve, 0));
        await refetch();
    };

    /**
     * Operations are referenced by Devices, Parts and Job Schedules, so deactivating one
     * is warned about but never blocked (Story 1, AC3).
     */
    const confirmDeactivate = (operations, proceed) => {
        const isSingle = operations.length === 1;
        openConfirm({
            title: isSingle
                ? UI_STRINGS.DEACTIVATE_TITLE_SINGLE(operations[0].name)
                : UI_STRINGS.DEACTIVATE_TITLE_BULK(operations.length),
            content: (
                <div className="text-dark" style={{ whiteSpace: "pre-wrap" }}>
                    {UI_STRINGS.DEACTIVATE_WARNING}
                </div>
            ),
            confirmText: UI_STRINGS.DEACTIVATE_CONFIRM,
            cancelText: UI_STRINGS.DEACTIVATE_CANCEL,
            type: "warning",
            onConfirm: async () => {
                await proceed();
                closeConfirm();
            },
            onClose: closeConfirm,
        });
    };

    const applyToggleActive = async (operations, makeActive) => {
        const ids = operations.map((o) => o.id);
        setIsTogglingActive(true);
        try {
            await updateManyOperation({
                variables: {
                    data: makeActive ? restoreData() : softDeleteData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(
                makeActive
                    ? `Restored ${ids.length} operation(s)`
                    : `Deactivated ${ids.length} operation(s)`,
            );
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setIsTogglingActive(false);
        }
    };

    const handleToggleActive = async (operations, makeActive) => {
        if (!operations || operations.length === 0) return;
        if (makeActive) {
            await applyToggleActive(operations, true);
            return;
        }
        confirmDeactivate(operations, () =>
            applyToggleActive(operations, false),
        );
    };

    const handleToggleActiveSingle = async (operation) => {
        const deactivating = operation.is_active;

        const apply = async () => {
            setTogglingActiveId(operation.id);
            setIsTogglingActive(true);
            try {
                await updateManyOperation({
                    variables: {
                        data: deactivating ? softDeleteData() : restoreData(),
                        where: { id: { in: [operation.id] } },
                    },
                });
                await refetch();
            } catch (err) {
                starekContext.showError(err);
            } finally {
                setTogglingActiveId(null);
                setIsTogglingActive(false);
            }
        };

        if (!deactivating) {
            await apply();
            return;
        }
        confirmDeactivate([operation], apply);
    };

    const operationExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    if (loading) return <EntityLoadingState icon="fa-tasks" />;

    const activeOperation = routeEditOperation || selectedRows[0] || null;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={operationExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    patchSearchParams({ view: mode });
                }}
                onDelete={() => handleDelete(selectedRows)}
                onEdit={handleEditSelected}
            >
                <EntityOperationBar.View
                    mode="card"
                    icon="fa-th"
                    tooltip={UI_STRINGS.VIEW_MODE_CARD}
                >
                    <OperationsCardView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        onAdd={handleAdd}
                        onRowClicked={handleRowClicked}
                        onToggleActive={handleToggleActiveSingle}
                        togglingActiveId={togglingActiveId}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list-ul"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <OperationsListingView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                        noDataComponent={<EntityEmptyState icon="fa-tasks" entityName="Operation" messages={EMPTY_MESSAGES.operations} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {showAddOperationDrawer && (
                <OperationSidebar
                    show={true}
                    item={activeOperation}
                    plantId={starekContext.lastUsedPlant.id}
                    onDiscard={handleDrawerClose}
                    onSave={handleSaveOperation}
                    onDelete={(operation) => handleDelete([operation])}
                    onToggleActive={handleToggleActiveSingle}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting || isTogglingActive}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { Operations };
